import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, User as UserIcon, Mail, Camera, Loader2, Pencil, Check, X as XIcon } from "lucide-react";
import Sidebar from "../components/Sidebar";
import ConfirmDialog from "../components/ConfirmDialog";
import PlansDialog from "../components/PlansDialog";
import InstallAppButton from "../components/InstallAppButton";
import { useAuth } from "../lib/auth-context";
import { useHiddenTasks } from "../lib/hidden-tasks";
import { useTasks } from "../lib/tasks-context";
import { tokensToBRL, formatBRL } from "../lib/currency";
import { app as appApi } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_AVATAR = "/img/default-avatar.svg";
const DEFAULT_COVER = "/img/default-cover.svg";
const MAX_IMG = 10 * 1024 * 1024;

export default function SettingsPage() {
    const { user, tokensInfo, logout, refreshMe } = useAuth();
    const navigate = useNavigate();
    const { filter: filterHidden, hide: hideTask } = useHiddenTasks();
    const { tasksRaw, loading, removeTask, loadTasks } = useTasks();
    const tasks = React.useMemo(() => filterHidden(tasksRaw), [tasksRaw, filterHidden]);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [deleteAllOpen, setDeleteAllOpen] = useState(false);
    const [deletingAll, setDeletingAll] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [savingName, setSavingName] = useState(false);
    const nameRef = React.useRef(null);
    const avatarInputRef = React.useRef(null);
    const coverInputRef = React.useRef(null);

    const saveProfile = async (patch) => {
        // avatar_key/cover_key "keep" = não mexer nas fotos atuais
        await appApi.updateProfile({
            name: user?.name || "Usuário",
            avatar_url: "", avatar_key: "keep",
            cover_url: "", cover_key: "keep",
            ...patch,
        });
        await refreshMe();
    };

    const handlePhoto = async (e, kind) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem."); return; }
        if (f.size > MAX_IMG) { toast.error("A imagem não pode passar de 10MB."); return; }
        const setBusy = kind === "avatar" ? setUploadingAvatar : setUploadingCover;
        setBusy(true);
        try {
            const res = await appApi.upload([f], "profile");
            const up = res?.uploaded?.[0];
            if (!up) throw new Error();
            if (kind === "avatar") await saveProfile({ avatar_url: up.url, avatar_key: up.key });
            else await saveProfile({ cover_url: up.url, cover_key: up.key });
        } catch {
            toast.error("Falha ao atualizar a foto.");
        } finally {
            setBusy(false);
        }
    };

    const confirmDeleteAll = async () => {
        setDeletingAll(true);
        try {
            await appApi.deleteAllTasks();
            loadTasks();
        } catch {
            toast.error("Erro ao apagar o histórico.");
        } finally {
            setDeletingAll(false);
            setDeleteAllOpen(false);
        }
    };

    const saveName = async () => {
        const n = (nameRef.current?.value || "").trim();
        if (!n) { toast.error("Informe um nome válido."); return; }
        setSavingName(true);
        try {
            await saveProfile({ name: n });
            setEditingName(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao salvar o nome.");
        } finally {
            setSavingName(false);
        }
    };

    const doLogout = () => {
        logout();
        toast.success("Sessão encerrada.");
        navigate("/login");
    };

    const confirmDeleteTask = () => {
        if (!taskToDelete) return;
        hideTask(taskToDelete.id);
        removeTask(taskToDelete.id);
        setTaskToDelete(null);
        toast.success("Conversa excluída.");
    };

    return (
        <div className="flex h-[100dvh] overflow-hidden bg-background text-foreground">
            <Sidebar
                onNewChat={() => navigate("/chat")}
                tasks={tasks}
                loading={loading}
                currentTaskId={null}
                onSelect={(t) => navigate(`/chat/${t.id}`)}
                onLogoutRequest={() => setLogoutConfirmOpen(true)}
                onDelete={(t) => setTaskToDelete(t)}
            />

            <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
                <header className="sticky top-0 z-20 h-12 md:h-14 border-b border-border bg-[#0A0A0A]/85 backdrop-blur-xl flex items-center px-4 sm:px-6">
                    <button
                        onClick={() => navigate("/chat")}
                        className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors mr-1"
                        data-testid="settings-back-btn"
                        aria-label="Voltar"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    <h1 className="font-display font-bold text-lg tracking-tight">Configurações</h1>
                </header>

                {/* Profile hero — capa + avatar + nome (estilo rede social) */}
                <div className="relative border-b border-border">
                    <div className="relative h-44 sm:h-52 overflow-hidden group/cover">
                        <img
                            src={user?.cover_url || DEFAULT_COVER}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="profile-cover-img"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                        <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e, "cover")} data-testid="profile-cover-input" />
                        <button
                            type="button"
                            onClick={() => coverInputRef.current?.click()}
                            disabled={uploadingCover}
                            data-testid="profile-cover-btn"
                            className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm border border-border text-foreground rounded-full px-3 py-1.5 text-xs font-semibold hover:border-primary/50 transition-colors disabled:opacity-60"
                        >
                            {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                            Alterar capa
                        </button>
                    </div>
                    <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-8 pb-5 -mt-12">
                        <div className="flex items-end gap-5">
                            <div className="relative shrink-0">
                                <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e, "avatar")} data-testid="profile-avatar-input" />
                                <div className="w-24 h-24 rounded-full border-4 border-background overflow-hidden bg-muted" data-testid="settings-avatar">
                                    <img src={user?.avatar_url || DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => avatarInputRef.current?.click()}
                                    disabled={uploadingAvatar}
                                    data-testid="profile-avatar-btn"
                                    className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center border-2 border-background hover:opacity-90 transition-opacity disabled:opacity-60"
                                    aria-label="Alterar foto de perfil"
                                >
                                    {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={2.2} />}
                                </button>
                            </div>
                            <div className="pb-1 min-w-0 flex-1">
                                {editingName ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={nameRef}
                                            defaultValue={user?.name || ""}
                                            maxLength={80}
                                            autoFocus
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveName(); } if (e.key === "Escape") setEditingName(false); }}
                                            data-testid="profile-name-input"
                                            className="bg-[#0a0a0a] border border-border rounded-md px-3 py-1.5 text-lg font-bold outline-none focus:border-primary/60 transition-colors w-full max-w-xs"
                                        />
                                        <button type="button" onClick={saveName} disabled={savingName} data-testid="profile-name-save"
                                            className="p-2 rounded-full bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-60" aria-label="Salvar nome">
                                            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
                                        </button>
                                        <button type="button" onClick={() => setEditingName(false)} data-testid="profile-name-cancel"
                                            className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors" aria-label="Cancelar">
                                            <XIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 min-w-0">
                                        <h2 className="font-display text-2xl font-bold tracking-tight truncate" data-testid="settings-user-name">
                                            {user?.name || "Usuário"}
                                        </h2>
                                        <button type="button" onClick={() => setEditingName(true)} data-testid="profile-name-edit"
                                            className="p-1.5 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted transition-colors shrink-0" aria-label="Editar nome">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                                <p className="text-sm text-muted-foreground truncate" data-testid="settings-user-email">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto w-full p-4 sm:p-8 space-y-6">
                    {/* Tokens Card */}
                    <section className="border border-border rounded-sm bg-card overflow-hidden">
                        <div className="p-5 border-b border-border flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <Coins className="w-5 h-5 text-primary" />
                                <h3 className="font-display text-base font-semibold tracking-tight">Saldo e Consumo</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPlansOpen(true)}
                                data-testid="settings-recharge-btn"
                                className="flex items-center gap-1.5 bg-primary hover:opacity-90 text-white rounded-full px-3 py-1.5 transition-opacity"
                            >
                                <Coins className="w-3.5 h-3.5" strokeWidth={2.2} />
                                <span className="text-xs font-semibold">Recarregar</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-2 divide-x divide-border">
                            <div className="p-5">
                                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">Saldo atual</div>
                                <div className="font-display text-4xl font-black tracking-tighter text-primary" data-testid="settings-tokens-balance">
                                    {tokensInfo?.unlimited ? "∞" : (tokensInfo?.tokens ?? user?.tokens ?? 0).toLocaleString("pt-BR")}
                                </div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">tokens disponíveis</div>
                                {!tokensInfo?.unlimited && (
                                    <div className="text-sm font-mono font-semibold text-primary/90 mt-2" data-testid="settings-tokens-brl">
                                        ≈ {formatBRL(tokensToBRL(tokensInfo?.tokens ?? user?.tokens ?? 0))}
                                    </div>
                                )}
                            </div>
                            <div className="p-5">
                                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">Custo por mensagem</div>
                                <div className="font-display text-4xl font-black tracking-tighter">
                                    {tokensInfo?.task_cost ?? 50}
                                </div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">tokens por tarefa</div>
                            </div>
                        </div>
                    </section>

                    {/* Info rows */}
                    <section className="border border-border rounded-sm bg-card">
                        <Row icon={<Mail className="w-4 h-4" />} label="E-mail" value={user?.email || "—"} testId="row-email" />
                        <Row icon={<UserIcon className="w-4 h-4" />} label="Nome" value={user?.name || "Não definido"} testId="row-name" last />
                    </section>

                    {/* Zona de perigo */}
                    <section className="border border-red-500/25 rounded-sm bg-card">
                        <div className="flex items-center justify-between gap-4 px-5 py-4">
                            <div>
                                <div className="text-sm font-semibold">Histórico de conversas</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Apaga todas as suas conversas. Sem volta.</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDeleteAllOpen(true)}
                                data-testid="delete-all-history-btn"
                                className="shrink-0 px-4 py-2 rounded-full border border-red-500/40 text-red-400 text-xs font-semibold hover:bg-red-500/10 transition-colors"
                            >
                                Apagar tudo
                            </button>
                        </div>
                    </section>

                    <div className="flex justify-center">
                        <InstallAppButton className="flex items-center gap-2 bg-primary hover:opacity-90 text-white rounded-md px-5 py-2.5 transition-opacity text-sm font-semibold" />
                    </div>
                </div>
            </main>

            <ConfirmDialog
                open={logoutConfirmOpen}
                title="Sair da conta?"
                description="Você precisará fazer login novamente para acessar suas conversas."
                confirmLabel="Sim, sair"
                cancelLabel="Cancelar"
                onCancel={() => setLogoutConfirmOpen(false)}
                onConfirm={doLogout}
                destructive
                testId="settings-logout-confirm"
            />

            <ConfirmDialog
                open={!!taskToDelete}
                title="Excluir conversa?"
                description={
                    taskToDelete
                        ? `"${taskToDelete.title || "Conversa sem título"}" será removida da sua lista. Esta ação não pode ser desfeita.`
                        : ""
                }
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                onCancel={() => setTaskToDelete(null)}
                onConfirm={confirmDeleteTask}
                destructive
                testId="settings-delete-task-confirm"
            />

            <ConfirmDialog
                open={deleteAllOpen}
                title="Apagar todo o histórico?"
                description="Todas as conversas serão apagadas. Sem volta."
                confirmLabel={deletingAll ? "Apagando..." : "Apagar tudo"}
                cancelLabel="Cancelar"
                onCancel={() => setDeleteAllOpen(false)}
                onConfirm={confirmDeleteAll}
                destructive
                testId="settings-delete-all-confirm"
            />

            <PlansDialog open={plansOpen} onClose={() => setPlansOpen(false)} />
        </div>
    );
}

function Row({ icon, label, value, testId, last }) {
    return (
        <div
            className={`flex items-center gap-4 px-5 py-4 ${last ? "" : "border-b border-border"}`}
            data-testid={testId}
        >
            <span className="text-muted-foreground">{icon}</span>
            <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
                <div className="text-sm mt-0.5">{value}</div>
            </div>
        </div>
    );
}
